import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import axios from '~/axiosWrapper';

import useDidMountEffect from '~/app/hooks/useDidMountEffect';

import Field, { FieldProps } from './Field';
import useField from './useField';
import Options from './Options';
import useDefaultValue from './useDefaultValue';

import classes from './style.module.scss';

type SelectOption = {
  text: React.ReactNode,
  value: string | number
}

export interface SelectPropsDefault extends React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> {
  ref?: React.Ref<HTMLSelectElement>
  options?: SelectOption[]
  optionsUrl?: string
  showEmptyOption?: boolean
  emptyOptionValue?: string | number
  defaultValue?: string | number
  value?: string | number
}

export type SelectProps = SelectPropsDefault & FieldProps

export type SelectComponent = React.RefForwardingComponent<HTMLSelectElement, SelectProps>

const Select: SelectComponent = (props, ref) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [options, setOptions] = useState<SelectOption[]>([]);
  // console.log('tttt', props, ref)

  const {
    fieldProps: { icon = 'arrow_drop_down', iconPosition = 'right', iconSize = 32, ...fieldProps },
    inputProps: { defaultValue: defaultDefaultValue, options: defaultOptions, optionsUrl, onChange, placeholder, showEmptyOption, emptyOptionValue, ...othersInputProps }
  } = useField<SelectPropsDefault>(props);

  const defaultValue = useDefaultValue(defaultDefaultValue);

  const [value, setValue] = useState<number | string>(defaultValue);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const notifyChange = useCallback((fieldName: string | undefined, nextValue: number | string): void => {
    const handler = onChangeRef.current;
    if (handler && fieldName) {
      // @ts-ignore — synthetic event for useForm fieldEvents.onChange
      handler({ target: { name: fieldName, value: nextValue, type: 'select-one' } });
    }
  }, []);

  const setNewOptions = useCallback((data: any[]): void => {
    setOptions(data.map(item => ({ text: item.name, value: item.id })));

    if (!defaultValue && data.length) {
      const firstId = data[0].id;
      setValue(firstId);
      notifyChange(othersInputProps.name, firstId);
    }
  }, [defaultValue, notifyChange, othersInputProps.name]);

  const selectChangeHandler = useCallback((e: React.ChangeEvent<HTMLSelectElement>): void => {
    const next = +e.target.value;
    setValue(next);
    notifyChange(othersInputProps.name, next);
  }, [notifyChange, othersInputProps.name]);

  useDidMountEffect(() => {
    if (optionsUrl) {
      axios.get<any[], any[]>(optionsUrl)
        .then(setNewOptions)
        .finally(() => {
          setLoading(false);
        });
    } else if (defaultOptions) {
      setOptions(defaultOptions);
      setLoading(false);
    }
  });

  useEffect(() => {
    if (!optionsUrl && defaultOptions) {
      setOptions(defaultOptions);
      setLoading(false);
    }
  }, [defaultOptions, optionsUrl]);

  useEffect(() => {
    if (defaultDefaultValue !== undefined && defaultDefaultValue !== null) {
      const nextValue = Array.isArray(defaultDefaultValue) ? defaultDefaultValue[0] : defaultDefaultValue;
      if (nextValue !== undefined) {
        setValue(prev => {
          if (String(prev) !== String(nextValue)) {
            notifyChange(othersInputProps.name, nextValue);
            return nextValue;
          }
          return prev;
        });
      }
    }
  }, [defaultDefaultValue, notifyChange, othersInputProps.name]);

  useEffect(() => {
    notifyChange(othersInputProps.name, defaultValue);
    // Register initial value once on mount (form store may not receive DOM events otherwise).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Field
      icon={icon}
      iconPosition={iconPosition}
      iconSize={iconSize}
      {...fieldProps}
      loading={fieldProps.loading || loading}
    >
      <select
        ref={ref}
        className={`${classes['input']} ${classes['select']}`}
        {...othersInputProps}
        value={othersInputProps.value !== undefined ? othersInputProps.value : value}
        disabled={fieldProps.loading || loading}
        onChange={selectChangeHandler}
      >
        {!!placeholder && !!showEmptyOption && <option key="" value={emptyOptionValue} disabled>{placeholder}</option>}
        <Options options={options} />
        {/*{options.map(option => <option key={option.value} value={option.value}>{option.text}</option>)}*/}
      </select>
    </Field>
  );
};

export default memo(React.forwardRef(Select))
